export type ProvisioningStatusItem = {
  device_id?: string;
  org_id?: string;
  site_id?: string;
  provisioning_status?: string;
  [key: string]: unknown;
};

function scopeKey(orgId?: string | null, siteId?: string | null, deviceId?: string | null): string {
  return `${String(orgId || '').trim()}|${String(siteId || '').trim()}|${String(deviceId || '').trim()}`;
}

function isSandboxMirror(status?: string | null): boolean {
  return String(status || '').trim().toLowerCase() === 'sandbox_mirror';
}

function isActivated(status?: string | null): boolean {
  return String(status || '').trim().toLowerCase() === 'activated';
}

/**
 * Merge provisioning status_batch items into a scoped devices list.
 * Prefer org+site+device keys; never let sandbox_mirror overwrite an activated row.
 */
export function mergeProvisioningStatusIntoDevices<T extends ProvisioningStatusItem>(
  baseDevices: T[],
  statusItems: ProvisioningStatusItem[],
  scope?: { org_id?: string | null; site_id?: string | null }
): T[] {
  const scopedOrg = String(scope?.org_id || '').trim();
  const scopedSite = String(scope?.site_id || '').trim();
  const byScopeKey = new Map<string, ProvisioningStatusItem>();
  const byDeviceId = new Map<string, ProvisioningStatusItem>();

  for (const item of statusItems || []) {
    const deviceId = String(item?.device_id || '').trim();
    if (!deviceId) continue;
    const itemOrg = String(item?.org_id || scopedOrg || '').trim();
    const itemSite = String(item?.site_id || scopedSite || '').trim();
    if (itemOrg && itemSite) {
      byScopeKey.set(scopeKey(itemOrg, itemSite, deviceId), item);
    }

    const existing = byDeviceId.get(deviceId);
    if (!existing) {
      byDeviceId.set(deviceId, item);
      continue;
    }
    // Prefer activated over sandbox_mirror for device_id-only collisions.
    if (isSandboxMirror(item.provisioning_status) && isActivated(existing.provisioning_status)) {
      continue;
    }
    if (isActivated(item.provisioning_status) && isSandboxMirror(existing.provisioning_status)) {
      byDeviceId.set(deviceId, item);
      continue;
    }
    byDeviceId.set(deviceId, item);
  }

  return baseDevices.map((device) => {
    const deviceId = String(device?.device_id || '').trim();
    if (!deviceId) return device;
    const deviceOrg = String(device?.org_id || scopedOrg || '').trim();
    const deviceSite = String(device?.site_id || scopedSite || '').trim();
    const scopedHit =
      deviceOrg && deviceSite
        ? byScopeKey.get(scopeKey(deviceOrg, deviceSite, deviceId))
        : undefined;
    const hit = scopedHit || byDeviceId.get(deviceId);
    if (!hit) return device;

    const baseStatus = device.provisioning_status;
    const hitStatus = hit.provisioning_status;
    if (isActivated(baseStatus) && isSandboxMirror(hitStatus)) {
      return device;
    }
    return { ...device, ...hit };
  });
}
